/* eslint-disable indent */
import Card from '@/components/Card';
import { FormField } from '@/components/Form/FormField';
import { FieldValues } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { StatusTag } from '@/components/Tag/StatusTag';
import { HumanModerationStatus } from '@/api/entities/moderation';
import { FormFieldReportSection } from '@/components/Form/FormFieldReportSection';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { getFormattedTime } from '@/utils/date.utils';
import dayjs from 'dayjs';
import { useGetUserById } from '@/hooks/user.hooks';
import { Link } from 'react-router-dom';
import { useGetEntityRoleByUserId } from '@/hooks/user-entity-role.hooks';
import { getPreparedEntityRoleLabel } from '@/utils/user-entity-role';
import Loader from '@/components/Loading/Loader';
import { useUserRole } from '@/hooks/auth.hooks';
import { twMerge } from 'tailwind-merge';
import { useGetVehiculeCheckTypeById } from '@/hooks/vehicule-check-type.hooks';
import { getTranslatedItem } from '@/utils/language.utils';

interface VehiculeCheckViewInfosProps<TSchema extends FieldValues>
  extends FormManagementProps<TSchema> {
  constainsOnlyClientName?: boolean;
}

export const VehiculeCheckViewInfos = <TSchema extends FieldValues>({
  t,
  data,
  isReadOnly,
  constainsOnlyClientName,
}: VehiculeCheckViewInfosProps<TSchema>) => {
  const { isAdmin } = useUserRole();

  const { data: professionalUser, isLoading: isLoadingProfessionalUser } =
    useGetUserById(data?.actionUserId);

  const { data: entityRole, isLoading: isLoadingEntityRole } =
    useGetEntityRoleByUserId(data?.actionUserId);

  const { fullEntityRoleLabel } = getPreparedEntityRoleLabel({
    t,
    role: entityRole?.data,
  });

  const isIndividualUser = data?.actionUserType === ActionUserType.INDIVIDUAL;

  const isToModerate =
    data?.humanModerationStatus === HumanModerationStatus.A_MODERER;

  const userFullname = constainsOnlyClientName
    ? `${data?.individualFirstname} ${data?.individualLastname}`
    : `${professionalUser?.data?.firstname} ${professionalUser?.data?.lastname}`;

  const { data: vehiculeCheckType, isLoading: isLoadingVehiculeCheckType } =
    useGetVehiculeCheckTypeById(data?.vehiculeCheckTypeId);

  const translatedVehiculeCheckType = getTranslatedItem(
    vehiculeCheckType?.data?.['translatedVehiculeCheckTypes']
  );

  return (
    <>
      <Card
        title={
          <TitleSection
            text={t('common:informations')}
            defaultIconClassName="ki-badge"
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <>
          <FormFieldReportSection>
            {isAdmin && (
              <FormField
                text={`${t('inspectionId')} :`}
                textClassName="font-semibold text-slate-500"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  <p className="text-sm text-black">{data?.id}</p>
                </div>
              </FormField>
            )}
            <FormField
              text={`${t('common:referenceNumber_short_2')} :`}
              textClassName="font-semibold text-slate-500"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <p className="text-sm">{data?.referenceNumber || '--'}</p>
              </div>
            </FormField>
            <FormField
              text={`${t('common:date')} :`}
              textClassName="font-semibold text-slate-500"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <p className="text-sm">
                  {data?.createdAt
                    ? `${dayjs(data?.createdAt).format('DD/MM/YYYY')} ${t(
                        'common:at'
                      ).toLowerCase()} ${getFormattedTime(data?.createdAt)}`
                    : '--'}
                </p>
              </div>
            </FormField>
          </FormFieldReportSection>
          <FormFieldReportSection>
            <FormField
              text={`${t('common:performedBy')} :`}
              textWrapperClassName="mt-1"
              textClassName="font-semibold text-slate-500"
            >
              <StatusTag
                text={`${
                  isIndividualUser
                    ? t('common:individual')
                    : t('common:professional')
                }`}
                className={'text-purple-600 border-purple-400 bg-purple-100/40'}
              />
            </FormField>
            {!isLoadingProfessionalUser && (
              <FormField
                text={`${t('common:by')} :`}
                containerClassName="mt-2.5"
                textWrapperClassName="mt-1"
                textClassName="font-semibold text-slate-500"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center">
                    <div className="flex items-center gap-1.5">
                      <i className="-mt-0.75 ki-filled ki-user text-slate-500 text-[18px]" />
                      <div className="text-[13.7px] text-slate-600">
                        {userFullname}
                      </div>
                    </div>
                    {!isReadOnly && (
                      <Link
                        to={
                          constainsOnlyClientName
                            ? `/vehicule-check/view/${data?.id}?tab=client`
                            : `/user/edit/${data?.actionUserId}`
                        }
                        className="ml-[8px] px-[5px] pt-1 pb-[5.5px] flex justify-center items-center bg-primary rounded-lg shadow-md hover:cursor-pointer hover:opacity-80"
                      >
                        <i
                          className={twMerge(
                            'ki-outline ki-cursor text-white text-[18px]',
                            constainsOnlyClientName && 'ki-right-square'
                          )}
                        />
                      </Link>
                    )}
                  </div>
                  {!isReadOnly &&
                    !isLoadingEntityRole &&
                    fullEntityRoleLabel && (
                      <>
                        <div className="text-slate-400/45 font-light">|</div>
                        <StatusTag
                          text={fullEntityRoleLabel}
                          className={
                            'text-slate-600 border-slate-400/50 bg-transparent py-[4.5px]'
                          }
                        />
                      </>
                    )}
                </div>
              </FormField>
            )}
          </FormFieldReportSection>
          <FormFieldReportSection>
            <FormField
              text={`${t('common:inspectionType')} :`}
              containerClassName="items-center"
              textClassName="font-semibold text-slate-500"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                {!isLoadingVehiculeCheckType && (
                  <p className="text-[13.7px] text-slate-700">
                    {translatedVehiculeCheckType?.name ||
                      vehiculeCheckType?.data?.name}
                  </p>
                )}
              </div>
            </FormField>
          </FormFieldReportSection>
          {!isReadOnly && (
            <FormFieldReportSection>
              <FormField
                text={`${t('common:moderationStatus')} :`}
                containerClassName="items-center"
                textClassName="font-semibold text-slate-500"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  <StatusTag
                    text={`${
                      isToModerate
                        ? t('common:toModerate')
                        : t('common:moderated')
                    }`}
                    className={
                      isToModerate
                        ? 'bg-red-FF2929/10 text-red-FF2929'
                        : 'bg-green-EAFFF2 border-green-400 text-green-11BFA4 tracking-[0.005em]'
                    }
                  />
                </div>
              </FormField>
            </FormFieldReportSection>
          )}
        </>
      </Card>
      {(isLoadingProfessionalUser || isLoadingEntityRole) && <Loader />}
    </>
  );
};
